// listViewItemData.js

// Utilities
import { formatPrice, resolveFieldCurrencyCode } from 'utilities/Currency';
import { normalizeEmailValueForStorage } from 'utilities/Email';
import { getEnumOptionColor, getEnumOptionIconKey, getEnumOptionLabel } from 'utilities/EnumOptions';
import { isEnumCompatibleFieldType, isRelationFieldType } from 'utilities/FieldTypes';
import { formatLocationDisplayValue } from 'utilities/Location';
import { formatMeasurementValue } from 'utilities/Measurement';
import { normalizeNodelistKeys } from 'utilities/NodelistField';
import { formatPhoneValueForInput } from 'utilities/Phone';
import { extractRichTextPlainText } from 'utilities/RichText';
import { formatSignatureFieldDisplay } from 'utilities/SignatureFieldValue';
import {
  DEFAULT_SUBTITLE_FIELD_KEY,
  getNodeSubtitle,
  getViewIncludeSubtitle,
  isSubtitleFieldKey,
  isSubtitleFieldType,
  LEGACY_SUBTITLE_FIELD_KEY,
} from 'utilities/NodeSubtitle';
import { isCatchAllView } from 'utilities/ViewRows';
import {
  isSchemaFieldHiddenFromDisplay,
  isSchemaFieldShownInDefaultLists,
} from 'utilities/schemaFieldVisibility';
import { filterSecuredSchemaFields, resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';
import { buildStandardFieldMap } from 'views/table/utils/tableFieldUtils';
import { formatTemporalFieldValue, isFirestoreTimestampObject, isTemporalFieldType } from 'views/utils/temporalFieldFormat';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';
import { getPeopleProfiles } from 'views/utils/peopleFieldProfiles';

const LIST_VIEW_SUMMARY_MAX_CHARS = 240;

function summarizeListViewText(value, maxChars = LIST_VIEW_SUMMARY_MAX_CHARS) {
  const collapsed = String(value || '').replace(/\s+/g, ' ').trim();
  if (!collapsed) return '';
  if (collapsed.length <= maxChars) return collapsed;
  return `${collapsed.slice(0, maxChars).trimEnd()}…`;
}

const CATCH_ALL_LIST_AUTO_FIELD_TYPES = new Set([
  'string',
  'enum', 'pipeline',
  'people',
  'email',
  'annualdate',
  'date',
  'data',
  'datetime',
  'daterange',
  'datetimerange',
  'boolean',
  'number',
  'measurement',
  'phone',
  'percentage',
  'relation',
  'richtext',
  'signature',
  'money',
  'currency',
  'dateCreated',
  'dateModified',
  'location',
]);

const buildFields = (typeDef) => {
  // Prefer the shared display resolver so Admin Visible / Hidden apply here too.
  const displayFields = resolveDisplaySchemaFields(typeDef, { includeHidden: false });
  if (displayFields.length) return displayFields;

  if (Array.isArray(typeDef?.schema?.fields) && typeDef.schema.fields.length) {
    return filterSecuredSchemaFields(typeDef.schema.fields)
      .filter((field) => !isSchemaFieldHiddenFromDisplay(field));
  }

  return [];
};

const buildSchemaFieldMap = (fields) => {
  const map = buildStandardFieldMap();

  (Array.isArray(fields) ? fields : []).forEach((field) => {
    const key = field?.name || field?.key;
    if (key) map.set(key, field);
  });

  return map;
};

const resolveFieldValue = ({ node, fieldDef, typeDef }) => {
  if (!node || !fieldDef) return undefined;

  const fieldKey = fieldDef?.name || fieldDef?.key || '';
  const fieldType = String(fieldDef?.type || '').trim().toLowerCase();
  if (!fieldKey) return undefined;

  if (fieldKey === 'title') return node?.title;
  if (isSubtitleFieldKey(fieldKey, fieldType)) return getNodeSubtitle(node);
  if (fieldKey === 'tags' || fieldType === 'tags') return node?.tags;
  if (fieldKey === 'dateCreated') return node?.dateCreated;
  if (fieldKey === 'dateModified') return node?.dateModified;

  return resolveSchemaFieldValue({
    item: node,
    fieldDef,
    typeKey: typeDef?.key,
    dataSection: typeDef?.dataSection,
  });
};

const formatFieldValue = ({ fieldDef, fieldKey, language, resolveNodelistTitle, value }) => {
  if (value == null || value === '') return '';

  const fallbackType = fieldKey === 'dateCreated' || fieldKey === 'dateModified'
    ? 'date'
    : '';
  const fieldType = String(fieldDef?.type || fallbackType).toLowerCase();
  const isTimestampObject = isFirestoreTimestampObject(value);

  if (fieldType === 'boolean') return value ? 'Yes' : 'No';
  if (fieldType === 'money') {
    if (value == null || value === '') return '';
    return formatPrice(value, resolveFieldCurrencyCode(fieldDef));
  }

  if (fieldType === 'currency') {
    const code = String(value || '').trim().toUpperCase();
    return code || '';
  }

  if (fieldType === 'percentage') {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '';
    const clamped = Math.max(0, Math.min(100, Math.round(numeric)));
    return `${clamped}%`;
  }

  if (fieldType === 'number') {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric.toLocaleString();
    return String(value);
  }

  if (fieldType === 'measurement') return formatMeasurementValue(value);
  if (fieldType === 'email') return normalizeEmailValueForStorage(value);
  if (fieldType === 'phone') return formatPhoneValueForInput(value);

  if (fieldType === 'location') return formatLocationDisplayValue(value);

  if (isTemporalFieldType(fieldType) || isTimestampObject) {
    return formatTemporalFieldValue({
      value,
      fieldType,
      locale: 'en-US',
      dateOptions: {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      },
      dateTimeOptions: {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      },
    });
  }

  if (isEnumCompatibleFieldType(fieldType)) {
    return getEnumOptionLabel(fieldDef?.options || [], value, language);
  }

  if (isRelationFieldType(fieldDef) || fieldType === 'relation') {
    const keys = normalizeNodelistKeys(value);
    if (!keys.length) return '';
    if (typeof resolveNodelistTitle !== 'function') return '';

    return keys
      .map((key) => String(resolveNodelistTitle(key) || '').trim())
      .filter(Boolean)
      .join(', ');
  }

  if (fieldType === 'richtext') return extractRichTextPlainText(value);

  if (fieldType === 'signature') return formatSignatureFieldDisplay(value);

  if (fieldType === 'checklist' || value?.kind === 'checklist') {
    const itemTexts = Array.isArray(value?.itemTexts)
      ? value.itemTexts.map((entry) => String(entry || '').trim()).filter(Boolean)
      : [];
    if (itemTexts.length) return itemTexts.join(', ');

    const totalItems = Number(value?.totalItems) || 0;
    const completedItems = Number(value?.completedItems) || 0;
    if (totalItems > 0) return `${completedItems}/${totalItems}`;
    return '';
  }

  if (fieldType === 'data') {
    if (typeof value === 'string') return value;

    if (Array.isArray(value) || (value && typeof value === 'object')) {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
  }

  if (fieldType === 'tags') {
    return Array.isArray(value) ? value.join(', ') : String(value);
  }

  if (Array.isArray(value)) return value.join(', ');

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

export const resolveListViewItemConfig = ({
  enforceExplicitProperties = false,
  forceDefaultFields = false,
  typeDef = null,
  view = null,
} = {}) => {
  const viewFieldKeys = Array.isArray(view?.properties)
    ? view.properties
      .map((field) => String(field || '').trim())
      .filter(Boolean)
    : [];

  const viewType = String(view?.type || '').trim().toUpperCase();
  const isListLikeView = viewType === 'LIST' || viewType === 'ALPHA_LIST';
  const isCatchAllListView = isCatchAllView(view) && isListLikeView;
  const shouldUseDefaultListFields = forceDefaultFields
    || (
      !enforceExplicitProperties
      && isListLikeView
      && viewFieldKeys.length === 0
    );

  const fields = buildFields(typeDef);
  const schemaFieldMap = buildSchemaFieldMap(fields);
  const selectedSubtitleFieldKey = viewFieldKeys.find((fieldKey) => {
    const fieldType = String(schemaFieldMap.get(fieldKey)?.type || '').trim().toLowerCase();
    return isSubtitleFieldType(fieldType);
  }) || '';

  const includeSubtitle = isCatchAllListView
    || shouldUseDefaultListFields
    || getViewIncludeSubtitle(view)
    || Boolean(selectedSubtitleFieldKey)
    || viewFieldKeys.includes(DEFAULT_SUBTITLE_FIELD_KEY)
    || viewFieldKeys.includes(LEGACY_SUBTITLE_FIELD_KEY);
  const includeCover = shouldUseDefaultListFields
    || Boolean(view?.includeCover)
    || viewFieldKeys.includes('cover')
    || viewFieldKeys.includes('coverUrl');
  const autoDefaultListFields = (isCatchAllListView || shouldUseDefaultListFields)
    ? fields
      .map((field) => {
        const key = String(field?.name || field?.key || '').trim();
        const fieldType = String(field?.type || '').trim().toLowerCase();
        if (!key || !CATCH_ALL_LIST_AUTO_FIELD_TYPES.has(fieldType)) return null;
        if (!isSchemaFieldShownInDefaultLists(field)) return null;
        return key;
      })
      .filter(Boolean)
    : [];

  const configuredFields = viewFieldKeys.filter((field) => {
    if (
      !field
      || field === 'title'
      || field === DEFAULT_SUBTITLE_FIELD_KEY
      || field === LEGACY_SUBTITLE_FIELD_KEY
      || field === selectedSubtitleFieldKey
      || field === 'cover'
      || field === 'coverUrl'
    ) {
      return false;
    }

    const fieldDef = schemaFieldMap.get(field);
    // Drop fields that are not in the visible schema (Admin Visible off).
    if (!fieldDef || isSchemaFieldHiddenFromDisplay(fieldDef)) return false;
    return true;
  });

  const listFields = Array.isArray(view?.properties)
    ? configuredFields
    : ((!isCatchAllListView && !shouldUseDefaultListFields)
      ? configuredFields
      : autoDefaultListFields.reduce((merged, fieldKey) => {
        if (!merged.includes(fieldKey)) merged.push(fieldKey);
        return merged;
      }, [...configuredFields]));

  return {
    subtitleFieldKey: selectedSubtitleFieldKey || (includeSubtitle ? DEFAULT_SUBTITLE_FIELD_KEY : ''),
    fields,
    includeCover,
    includeSubtitle,
    listFields,
    schemaFieldMap,
    typeDef,
  };
};


/**
 * Append connector custom fields (non-schema) as extra list rows when present.
 * Convention: any data_* section may carry customFields[fieldId] =
 * { id, name, type, display }. Same shape knowledge projection already reads.
 * Skip bag rows already represented by the type schema (typed form/list fields).
 *
 * @param {object} node
 * @param {Map<string, object>|null} [schemaFieldMap]
 * @returns {Array<{ kind: string, label: string, value: string }>}
 */
function buildNativeCustomFieldValues(node, schemaFieldMap = null) {
  const rows = [];
  const seen = new Set();
  const schemaNames = new Set();
  // Bag entries use origin property ids; schema fields use prop_* / field_* names.
  const schemaOriginFieldIds = new Set();
  if (schemaFieldMap instanceof Map) {
    schemaFieldMap.forEach((field, key) => {
      const name = String(key || '').trim().toLowerCase();
      if (name) {
        schemaNames.add(name);
        if (name.startsWith('prop_')) {
          schemaOriginFieldIds.add(name.slice('prop_'.length));
        }
        if (name.startsWith('field_')) {
          schemaOriginFieldIds.add(name.slice('field_'.length));
        }
      }
      const originId = String(field?.originFieldId || '').trim().toLowerCase();
      if (originId) schemaOriginFieldIds.add(originId);
    });
  }

  Object.keys(node || {}).forEach((key) => {
    if (!key.startsWith('data_')) return;
    const section = node[key];
    if (!section || typeof section !== 'object' || Array.isArray(section)) return;
    const bag = section.customFields;
    if (!bag || typeof bag !== 'object' || Array.isArray(bag)) return;

    Object.values(bag).forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const fieldId = String(entry.id || '').trim().toLowerCase();
      if (fieldId) {
        if (schemaNames.has(fieldId)) return;
        if (schemaOriginFieldIds.has(fieldId)) return;
        if (schemaNames.has(`prop_${fieldId}`)) return;
        if (schemaNames.has(`field_${fieldId}`)) return;
      }
      const label = String(entry.name || entry.label || '').trim();
      const rawDisplay = entry.display ?? entry.displayValue;
      const value = (
        rawDisplay == null || typeof rawDisplay === 'object'
          ? ''
          : String(rawDisplay)
      ).trim();
      // Skip broken legacy coercions until the record is re-pulled.
      if (!label || !value || value === '[object Object]') return;
      const dedupeKey = `${label.toLowerCase()}\0${value.toLowerCase()}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      rows.push({
        kind: 'text',
        label,
        value: summarizeListViewText(value),
      });
    });
  });

  return rows;
}


export const buildListViewItemData = ({
  config,
  language = 'en',
  node,
  resolveNodelistTitle = null,
} = {}) => {
  if (!node) {
    return {
      coverUrl: '',
      subtitle: '',
      enumPills: [],
      fieldValues: [],
    };
  }

  const safeConfig = config || {};
  const typeDef = safeConfig.typeDef || null;
  const schemaFieldMap = safeConfig.schemaFieldMap instanceof Map
    ? safeConfig.schemaFieldMap
    : buildStandardFieldMap();
  const listFields = Array.isArray(safeConfig.listFields) ? safeConfig.listFields : [];

  const fieldValues = listFields
    .map((fieldKey) => {
      const fieldDef = schemaFieldMap.get(fieldKey) || null;
      const fieldLabel = String(fieldDef?.label || '').trim();
      const rawValue = resolveFieldValue({ node, fieldDef, typeDef });
      const fieldType = String(fieldDef?.type || '').toLowerCase();

      if (isEnumCompatibleFieldType(fieldType) || fieldType === 'multiselect') return null;

      if (fieldType === 'people') {
        const profiles = getPeopleProfiles(rawValue);
        if (!profiles.length) return null;

        return {
          kind: 'people',
          label: fieldLabel,
          profiles,
        };
      }

      const value = formatFieldValue({
        fieldDef,
        fieldKey,
        language,
        resolveNodelistTitle,
        value: rawValue,
      });
      if (!value) return null;

      return {
        kind: 'text',
        label: fieldLabel,
        value: summarizeListViewText(value),
      };
    })
    .filter(Boolean);

  const enumPills = listFields.flatMap((fieldKey) => {
    const fieldDef = schemaFieldMap.get(fieldKey) || null;
    const fieldType = String(fieldDef?.type || '').toLowerCase();
    if (!isEnumCompatibleFieldType(fieldType) && fieldType !== 'multiselect') return [];

    const rawValue = resolveFieldValue({ node, fieldDef, typeDef });
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];

    return values.map((value, index) => {
      if (value == null) return null;

      const normalizedValue = typeof value === 'string'
        ? value.trim()
        : String(value).trim();
      if (!normalizedValue) return null;

      const label = getEnumOptionLabel(fieldDef?.options || [], normalizedValue, language);
      if (!label || !String(label).trim()) return null;

      return {
        color: getEnumOptionColor(fieldDef?.options || [], normalizedValue),
        iconKey: getEnumOptionIconKey(fieldDef?.options || [], normalizedValue),
        key: `${fieldKey}-${normalizedValue}-${index}`,
        label,
      };
    }).filter(Boolean);
  });

  const subtitleFieldDef = safeConfig.includeSubtitle
    ? (schemaFieldMap.get(safeConfig.subtitleFieldKey || DEFAULT_SUBTITLE_FIELD_KEY) || null)
    : null;
  const subtitle = safeConfig.includeSubtitle
    ? summarizeListViewText(formatFieldValue({
      fieldDef: subtitleFieldDef,
      fieldKey: safeConfig.subtitleFieldKey || DEFAULT_SUBTITLE_FIELD_KEY,
      language,
      value: resolveFieldValue({ node, fieldDef: subtitleFieldDef, typeDef }),
    }))
    : '';
  const coverUrl = safeConfig.includeCover && typeof node?.coverUrl === 'string'
    ? node.coverUrl.trim()
    : '';

  const nativeCustomFieldValues = buildNativeCustomFieldValues(node, schemaFieldMap);

  return {
    coverUrl,
    subtitle,
    enumPills,
    fieldValues: nativeCustomFieldValues.length
      ? [...fieldValues, ...nativeCustomFieldValues]
      : fieldValues,
  };
};